// BUILD-042 — HR area shell. Routes follow /hr/me's resolved surfaces (design.md §7): My HR
// (only when the caller has a personal HR record), My Team (Manager grant + reports), HR
// Administration (hr.org.manage or hr.grant.manage). Server re-authorizes every route regardless
// of what this component renders — a route being reachable here is UX convenience only
// (requirements AZ-10).
//
// UAT correction (2026-10-02, decision requirement #1): distinguish loading, denied/unresolved
// caller, backend unavailable, and a genuinely resolved caller with zero surfaces (e.g. the Owner
// before any personal HR record exists — the Owner's own authorized HR Administration surface
// must still be reachable, never masked behind a scary generic error or an unconditional My HR
// 404).
//
// UAT visual cleanup (2026-10-05): the horizontal My HR / My Team / HR Administration tab row
// that used to live on this page has moved into the left sidebar as a dropdown under HR
// (AppLayout.tsx) — matching how every other grouped destination in this app is navigated, and
// freeing this page's own vertical space for content instead of a second navigation layer. This
// component no longer resolves GET /hr/me itself; it reads the SAME resolution the sidebar reads
// from HrAccessContext (one shared call, see that file), and now only handles ROUTING + the
// non-happy-path states (loading/denied/unavailable/error/no-surface) for whichever /hr/* URL
// the sidebar (or a direct link) sent the caller to.
import { Suspense, lazy } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { useHrAccess } from './HrAccessContext';
import { MyHrTab } from './MyHrTab';
import { MyTeamTab } from './MyTeamTab';
import { HrAdminTab } from './HrAdminTab';
import { ReviewerWorkspace } from './ReviewerWorkspace';
import { BenefitsManagerWorkspace } from './BenefitsManagerWorkspace';
import { DirectoryTab } from './DirectoryTab';
import { HrContentColumn } from './workspace/HrWorkspaceFrame';
import { LOCAL_PREVIEW_ENABLED } from './localPreviewFlag';

// Local employee-workspace visual preview — see HrAccessContext.tsx-adjacent history. SYNTHETIC/
// VISUAL ONLY: demonstrates the full approved 16-section employee-workspace map with invented
// data and in-memory interactions; it never calls a real API and must never be mistaken for the
// real (server-authorized) screens below. Gated on an explicit DEV-only build-time flag
// (VITE_ENABLE_LOCAL_PREVIEW, set only in this app's DEV .env; see localPreviewFlag.ts - it is
// additionally tied to `import.meta.env.DEV`, so a production `vite build` can never include it).
// Mounted OUTSIDE the real-access gate below so it stays reachable even while real HR
// access/backend state is loading, denied, or unavailable; it never depends on GET /hr/me succeeding.
const SHOW_LOCAL_PREVIEW = LOCAL_PREVIEW_ENABLED;
const RosterPage = /* @__PURE__ */ lazy(() => import('./preview/RosterPage').then((m) => ({ default: m.RosterPage })));
const EmployeeWorkspace = /* @__PURE__ */ lazy(() => import('./preview/EmployeeWorkspace').then((m) => ({ default: m.EmployeeWorkspace })));

export function HrPage() {
  const { state, reload } = useHrAccess();
  const location = useLocation();

  // The local preview is reachable regardless of real-access state (see module comment above) —
  // still DEV-only (SHOW_LOCAL_PREVIEW), so it cannot appear in a shipped bundle regardless of
  // this early mount point.
  if (SHOW_LOCAL_PREVIEW && location.pathname.startsWith('/hr/preview')) {
    return (
      <Suspense fallback={<div className="p-6 text-gray-500">Loading preview…</div>}>
        <Routes>
          <Route path="/preview" element={<RosterPage />} />
          <Route path="/preview/employees/:employeeId" element={<EmployeeWorkspace />} />
        </Routes>
      </Suspense>
    );
  }

  // Directory (2026-10-07, Owner-requested): "a company directory for everyone to see
  // employees." Unlike My HR/My Team/HR Administration, this is intentionally NOT gated behind
  // GET /hr/me's resolved surfaces at all — it is meant to be visible company-wide, so it is
  // checked BEFORE the loading/denied/unavailable/error/no-surface branches below, and stays
  // reachable even if the real HR access resolution itself failed. Oct 20: it now shows real
  // people from GET /hr/directory (a strict work-contact allow-list); the server still
  // authenticates the caller, so a signed-out or disabled account gets no rows.
  if (location.pathname.startsWith('/hr/directory')) {
    return (
      <div className="min-h-full bg-[#eef1f6]">
        <div className="p-3 sm:p-6 min-w-0">
          <HrContentColumn>
            <DirectoryTab />
          </HrContentColumn>
        </div>
      </div>
    );
  }

  if (state.kind === 'loading') {
    return <div className="p-6 text-gray-500">Loading HR…</div>;
  }
  if (state.kind === 'denied' || state.kind === 'unavailable' || state.kind === 'error') {
    return (
      <div className="p-3 sm:p-6 max-w-[1100px]">
        <div className="text-red-700 mb-3">{state.message}</div>
        <button type="button" onClick={reload} className="text-sm text-[#4AABDE] hover:underline">
          Retry
        </button>
      </div>
    );
  }

  const { me } = state;

  // A resolved caller with NO REAL surface at all (no HR record, no Manager grant, no admin
  // authority) is a genuine, truthful empty state — never an error. Distinct from "denied"
  // above, which means the platform could not even resolve the caller to an Employee.
  const mayReviewCorrections = !!me.surfaces.mayReviewCorrections;
  // Oct 23: a benefit manager who is not an HR administrator has the Benefits workspace (not part of hrAdmin).
  const mayManageBenefits = !!me.surfaces.mayManageBenefits;
  if (!me.surfaces.myHr && !me.surfaces.myTeam && !me.surfaces.hrAdmin && !mayReviewCorrections && !mayManageBenefits) {
    return (
      <div className="p-3 sm:p-6 max-w-[1100px]">
        <div className="text-gray-500 mb-4">You do not have an HR record or any HR access yet.</div>
      </div>
    );
  }

  // The default landing route ("/") must go to a surface the caller ACTUALLY has, in priority
  // order My HR -> My Team -> HR Administration — never unconditionally to My HR, which is how
  // an Owner with hrAdmin:true but myHr:false (no personal HR record) previously hit a 404 on
  // first visit (decision requirement #1).
  const defaultTo = me.surfaces.myHr ? '/hr' : me.surfaces.myTeam ? '/hr/team' : me.surfaces.hrAdmin ? '/hr/admin' : mayManageBenefits && !mayReviewCorrections ? '/hr/benefits' : '/hr/corrections';
  // Oct 19 06:28: correction review has its own entry. An HR administrator is sent to the same panels inside HR Administration (request id
  // and filters preserved); a reviewer who is NOT an administrator gets the lite reviewer workspace; everyone else lands on their default.
  const adminCorrectionsTarget = location.pathname.startsWith('/hr/corrections/pending') ? '/hr/admin/pending' : '/hr/admin/correction-requests';

  // Owner-approved (2026-10-07): after an A/B comparison against My Team on the original
  // bg-gray-50, Chris chose the darker blue-grey (#eef1f6, one shade past the approved
  // reference's --u-bg #f4f6fa) for all three real HR screens — My HR, My Team, and HR
  // Administration. Scoped to HrPage's own OUTER wrapper (full-width, min-h-full so the color
  // fills the entire visible content area) rather than AppLayout.tsx's shared background, so it
  // still does not change the background behind Home/My Work/Business Health or any other
  // non-HR page — platform-wide adoption beyond HR was raised separately and remains explicitly
  // deferred (not in this UAT's scope; see checkpoint.md Open Technical Questions). The
  // max-width/centering/padding stays on an INNER wrapper so the color spans edge-to-edge while
  // content remains readably constrained.
  return (
    <div className="min-h-full bg-[#eef1f6]">
      {/* Oct 24 (Owner decision, layout foundation): NO page is centred as a whole any more. Every HR page uses the same gutters; pages with a secondary rail
          mount HrWorkspaceFrame (rail pinned beside the global nav + a left-aligned ~1100px content column), the rest use HrContentColumn. */}
      <div className="p-3 sm:p-6 min-w-0" data-testid="hr-content-frame">
        {/* Oct 20 (Owner): no global HR pilot / records-kept / Sandbox banner. Signing state (not set up, Sandbox, Live) is shown beside the signing action. */}
        <Routes>
          <Route path="/" element={me.surfaces.myHr ? <MyHrTab /> : <Navigate to={defaultTo} replace />} />
          {/* MyTeamTab.tsx now handles both the roster and an expanded person's detail in one
              mounted component — see that file's own header for why "/team" and "/team/:id"
              must resolve to the SAME component instance rather than two separate routes
              (expand/collapse must never re-fetch the already-loaded roster). */}
          <Route path="/team" element={me.surfaces.myTeam ? <HrContentColumn><MyTeamTab /></HrContentColumn> : <Navigate to={defaultTo} replace />} />
          <Route path="/team/:employeeId" element={me.surfaces.myTeam ? <HrContentColumn><MyTeamTab /></HrContentColumn> : <Navigate to={defaultTo} replace />} />
          <Route path="/admin/*" element={me.surfaces.hrAdmin ? <HrAdminTab isPrimaryOwner={me.isPrimaryOwner} viewerEmployeeId={me.employeeId} /> : <Navigate to={defaultTo} replace />} />
          {/* Oct 23: the Benefits workspace for a benefit manager. An HR administrator uses HR Administration > Benefit plans instead. */}
          <Route path="/benefits/*" element={
            me.surfaces.hrAdmin
              ? <Navigate to="/hr/admin/benefits" replace />
              : mayManageBenefits
                ? <HrContentColumn><BenefitsManagerWorkspace /></HrContentColumn>
                : <Navigate to={defaultTo} replace />
          } />
          <Route path="/corrections/*" element={
            me.surfaces.hrAdmin
              ? <Navigate to={{ pathname: adminCorrectionsTarget, search: location.search }} replace />
              : mayReviewCorrections
                ? <ReviewerWorkspace viewerEmployeeId={me.employeeId} />
                : <Navigate to={defaultTo} replace />
          } />
          {/* /hr/directory is handled by the early return above, before this block is reached — not duplicated here. */}
          <Route path="*" element={<Navigate to={defaultTo} replace />} />
        </Routes>
      </div>
    </div>
  );
}
